import { decodeId } from '@koob/utils';
import { ExtraScopeEnum } from '@koob/enums';
import { v4 as uuidv4 } from 'uuid';

// Only the two calls toTripState needs — not the full
// TripDesignerConfigApi (logic/ cannot import config/, a different layer).
interface FormatTripFromApiApi {
  getExperience: (params: {
    experienceId: string;
    locale?: string;
  }) => Promise<{ data: any }>;
  getHotel: (params: {
    hotelId: string;
    locale?: string;
  }) => Promise<{ data: any }>;
}

interface FormatTripFromApiParams {
  trip: any;
  api: FormatTripFromApiApi;
  locale?: string;
}

function collectExtras(trip: any) {
  const dayToExperienceMap: Record<number, string> = {};
  trip?.tripDays?.forEach((day: any) => {
    if (day.experienceId) {
      dayToExperienceMap[day.dayIndex] = day.experienceId;
    }
  });

  return [
    ...(trip?.globalOptions?.map((item: any) => ({
      ...item,
      // Look up the experienceId from the day this global option belongs to
      experienceId: dayToExperienceMap[item.dayIndex] ?? null,
      scope: ExtraScopeEnum.GLOBAL,
    })) ?? []),
    ...(trip?.tripDays?.flatMap((day: any) =>
      (day.options ?? []).map((item: any) => ({
        ...item,
        // Day options inherit experienceId from their parent day
        experienceId: day.experienceId ?? null,
        scope: ExtraScopeEnum.DAY,
      })),
    ) ?? []),
  ]?.filter(Boolean);
}

// Fetch all extra experience data in parallel. Guard each fetch: a single
// unfetchable option (e.g. a proposal/template referencing another
// organization's experience, which returns 403/404) must not reject the whole
// Promise.all and wipe every pre-filled product from the itinerary.
async function fetchExtraExperienceMap(
  allExtras: any[],
  api: FormatTripFromApiApi,
  locale: string | undefined,
) {
  const extraExperienceMap: Record<string, any> = {};

  await Promise.all(
    allExtras?.map(async (option: any) => {
      if (!option.optionId || extraExperienceMap[option.optionId]) {
        return;
      }
      if (option.isAccessible === false) {
        // Not distributed to us: the API already stripped it down to its
        // identification, so keep that and never fetch it.
        extraExperienceMap[option.optionId] = {
          ...(option.option ?? {}),
          isAccessible: false,
        };
        return;
      }
      try {
        const experience = await api.getExperience({
          experienceId: option.optionId,
          locale,
        });
        extraExperienceMap[option.optionId] = experience.data;
      } catch {
        // Skip this extra; the rest of the trip still loads.
      }
    }) ?? [],
  );

  return extraExperienceMap;
}

// Group extras by dayIndex (0-based) for easier lookup
function groupExtrasByDayIndex(
  allExtras: any[],
  extraExperienceMap: Record<string, any>,
) {
  const extrasByDayIndex: Record<number, any[]> = {};

  allExtras.forEach((option: any) => {
    const dayIdx = option.dayIndex - 1; // Convert 1-based to 0-based
    if (!extrasByDayIndex[dayIdx]) {
      extrasByDayIndex[dayIdx] = [];
    }
    const extraData = extraExperienceMap[option.optionId];
    if (!extraData) {
      return;
    }
    extrasByDayIndex[dayIdx].push({
      ...extraData,
      id: extraData.id,
      experienceId: option.experienceId, // Include experienceId for matching
      parentExtraId: option?.parent?.option?.id ?? undefined,
      dayIndex: dayIdx,
      scope: option.scope,
      adultCount: option.adultCount ?? undefined,
      childrenBirthdates: option.childrenBirthdates ?? undefined,
      // KD-3650 — lines saved before the ticket come back with a null key and
      // no rank: a local key is minted so they behave as a single occurrence.
      occurrenceKey: option.occurrenceKey ?? uuidv4(),
      rank: option.rank ?? 0,
      parentOccurrenceKey: option.parentOccurrenceKey ?? undefined,
    });
  });

  return extrasByDayIndex;
}

// Resolves a day's experience: the accessible-fetch path, or a 403/404
// fallback to the trip response's own embedded `day.experience`.
async function resolveDayExperience(
  day: any,
  api: FormatTripFromApiApi,
  locale: string | undefined,
) {
  if (day.experienceIsAccessible === false) {
    // The DMC never distributed this experience to us. The API already
    // sent back its identification (and nothing else), so keep it in the
    // itinerary and skip a fetch that would only 403.
    return { ...(day.experience ?? {}), isAccessible: false };
  }

  try {
    const experience = await api.getExperience({
      experienceId: day.experienceId,
      locale,
    });
    return experience?.data ?? null;
  } catch {
    // The experience can't be fetched on its own — typically a proposal
    // whose experiences belong to another organization (403/404). Fall
    // back to the experience the trip response already embeds
    // (`day.experience`) so the pre-filled product still shows in the
    // itinerary instead of leaving a blank day. Note: this embedded copy
    // has no `programs` (the trip endpoint does not populate them), so
    // program-derived details (location header, accommodation type) stay
    // empty until the DMC grants access.
    const experienceData = day.experience ?? null;
    if (!experienceData) {
      console.error(
        `Failed to load experience ${day.experienceId} for day ${day.dayIndex}`,
      );
    }
    return experienceData;
  }
}

function applyDayExperience(
  dayData: Record<string, any>,
  day: any,
  experienceData: any,
  experiences: Record<string, any>,
) {
  if (!experienceData?.id) {
    return;
  }

  // Store experience in the experiences map if not already present
  if (!experiences[experienceData.id]) {
    experiences[experienceData.id] = { ...experienceData, selectedExtras: [] };
  }

  // Set experience reference in day
  dayData.experienceId = experienceData.id;
  dayData.experienceDayIndex = day.experienceDayIndex + 1;
  dayData.experienceIsAccessible = experienceData.isAccessible !== false;
}

// A hotel already marked inaccessible: kept in the itinerary with its name
// and address only, so the day is never silently emptied. No price is
// fetched — the API would refuse, and there is nothing to show anyway.
function applyInaccessibleDayHotel(
  dayData: Record<string, any>,
  day: any,
  hotels: Record<string, any>,
) {
  // Decoded like the accessible branch: that one gets its id from the
  // hotels API (an encoded gid), this one straight from the trip response
  // (a numeric string), and both have to end up as the same number or a
  // lookup by hotel id would miss inaccessible hotels.
  const decodedHotelId = decodeId(day.hotelId);

  if (!hotels[decodedHotelId]) {
    hotels[decodedHotelId] = {
      ...(day.hotel ?? {}),
      id: decodedHotelId,
      isIncluded: false,
      isAccessible: false,
    };
  }
  dayData.hotelId = decodedHotelId;
  dayData.hotelIsAccessible = false;
  dayData.hotelIsIncluded = false;
}

async function applyAccessibleDayHotel(
  dayData: Record<string, any>,
  day: any,
  api: FormatTripFromApiApi,
  locale: string | undefined,
  experiences: Record<string, any>,
  hotels: Record<string, any>,
) {
  try {
    const hotel = await api.getHotel({ hotelId: day.hotelId, locale });
    const decodedHotelId = decodeId(hotel.data.id);

    const program =
      experiences[dayData.experienceId]?.programs?.[day.experienceDayIndex];
    const isIncludedHotel = program?.isIncludedAccomodation ?? false;

    // Store hotel in the hotels map if not already present
    if (!hotels[decodedHotelId]) {
      hotels[decodedHotelId] = {
        ...hotel.data,
        id: decodedHotelId,
        isIncluded: isIncludedHotel,
        // Preserve price from API if available
        price: hotel.data?.price,
        currency: hotel.data?.currency,
      };
    }

    // Set hotel reference in day
    dayData.hotelId = decodedHotelId;
    dayData.hotelIsIncluded = isIncludedHotel;

    // Preserve hotel pricing from API if available (only for non-included hotels)
    if (
      !isIncludedHotel &&
      hotel.data?.price !== undefined &&
      hotel.data?.currency
    ) {
      dayData.hotelPricing = {
        price: hotel.data.price,
        currency: hotel.data.currency,
      };
    }
  } catch {
    // Keep the day even if its hotel can't be fetched.
    console.error(
      `Failed to load hotel ${day.hotelId} for day ${day.dayIndex}`,
    );
  }
}

async function formatDay(
  day: any,
  api: FormatTripFromApiApi,
  locale: string | undefined,
  experiences: Record<string, any>,
  hotels: Record<string, any>,
  extrasByDayIndex: Record<number, any[]>,
) {
  const dayIdx = day.dayIndex - 1; // Convert 1-based to 0-based

  const dayData: Record<string, any> = {
    id: uuidv4(),
    dayIndex: day.dayIndex,
    experienceId: null,
    hotelId: null,
    experienceDayIndex: null,
    experiencePricing: null,
    hotelPricing: null,
    hotelBookedOnMyOwn: day.hotelBookedOnMyOwn ?? false,
    // Default true so a day with no product, or a DMC reading its own trip,
    // never renders the "not distributed" alert.
    hotelIsAccessible: true,
    experienceIsAccessible: true,
    hotelSelectedRoomKey: day.hotelSelectedRoomKey ?? null,
    roomsComposition: day.roomsComposition ?? null,
    skipTransitionOption: day.skipTransitionOption ?? false,
    // Pre-populate selectedExtras from saved data
    selectedExtras: extrasByDayIndex[dayIdx] ?? [],
  };

  if (day.experienceId) {
    const experienceData = await resolveDayExperience(day, api, locale);
    applyDayExperience(dayData, day, experienceData, experiences);
  }

  const hotelInaccessible =
    day.hotelIsAccessible === false || day.hotel?.isAccessible === false;

  if (day.hotelId && hotelInaccessible) {
    applyInaccessibleDayHotel(dayData, day, hotels);
  } else if (day.hotelId) {
    await applyAccessibleDayHotel(
      dayData,
      day,
      api,
      locale,
      experiences,
      hotels,
    );
  }

  return dayData;
}

// Base behaviour is TO's: a 403/404 while fetching a product falls back to
// the identification the trip response already embeds (`day.experience` /
// `day.hotel`), and every saved extra is pre-filled onto its day's
// `selectedExtras`. DMC's `organizationId`/`isTransitionalProgram` need no
// special-casing here — they ride along on the full experience spread above,
// same as every other field the API happens to send.
export async function toTripState({
  trip,
  api,
  locale,
}: FormatTripFromApiParams) {
  const experiences: Record<string, any> = {};
  const hotels: Record<string, any> = {};

  const allExtras = collectExtras(trip);
  const extraExperienceMap = await fetchExtraExperienceMap(
    allExtras,
    api,
    locale,
  );
  const extrasByDayIndex = groupExtrasByDayIndex(allExtras, extraExperienceMap);

  const days = await Promise.all(
    trip?.tripDays?.map((day: any) =>
      formatDay(day, api, locale, experiences, hotels, extrasByDayIndex),
    ) ?? [],
  );

  return {
    days: days.sort((a, b) => a.dayIndex - b.dayIndex),
    experiences,
    hotels,
    // initialExtras is now empty - extras are directly on days
    initialExtras: [] as any[],
  };
}
